import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWorkspaceDetail } from "../../../hooks/useWorkspaceDetail";
import { emptyAiStoryDetails } from "../../molecules/AiStoryDetailsStep";
import { useWorkspacePickerData } from "../../../hooks/useWorkspacePickerData";
import { DEFAULT_RECIPE_ID } from "../../molecules/RecipePickerStep";
import {
  FIRST_STEP_ID,
  NEW_PATH_FIRST_STEP_ID,
  getDataStoryStep,
  resolveStepPrimaryActionLabel,
  resolveStepTitle,
} from "./dataStoryStepRegistry";
import { toCreateStoryRequest } from "../../../services/dataStories/createStoryMappers";
import type {
  DataStoryRecipeId,
  DataStoryStepContext,
  DataStoryStepId,
  DataStoryStepSeed,
  RecipePickerValues,
  StoryDetailsValues,
} from "../../../services/dataStories/dataStoryFlow.types";
import type { CreateStoryRequest } from "../../../services/dataStories/storiesApi.types";

/**
 * Values for every step visited so far, keyed by step id.
 *
 * A map rather than one object because the submit payload spans steps: the
 * first step (the recipe, or on the new Story path the method) supplies
 * `creation_method`, the details step supplies the rest.
 * Keeping them separate also means going back to a step (when a Back
 * affordance exists) restores what was typed instead of resetting it.
 */
type ValuesByStep = Partial<Record<DataStoryStepId, unknown>>;

interface Args {
  open: boolean;
  onSubmit: (request: CreateStoryRequest) => void | Promise<void>;
  /** Workspace whose connectors and data models the sources picker offers. */
  workspaceId: string;
  /**
   * Opens on the new Story path's method step instead of the recipe picker. Its
   * blank canvas submits from that step; its AI method ends on the goal step.
   */
  newPath: boolean;
  /** The method highlighted when the flow opens (default: AI); the empty state's "Blank story" passes the blank canvas. */
  initialRecipeId?: DataStoryRecipeId;
}

/**
 * Details step ids, in the order the request builder should look for values.
 *
 * The flow visits at most one details step, so the first one present is the
 * one the user filled. Listing them here rather than reading a hard-coded key
 * means a new details frame needs no change to `buildRequest`.
 */
const DETAILS_STEP_IDS: DataStoryStepId[] = [
  "ai-details",
  "blank-details",
  "agent-details",
  "model-details",
];

/**
 * What a step being entered gets to initialise itself from.
 *
 * Read off the first step's values (the recipe picker, or the new path's method
 * picker, which hold the same selection) rather than tracked separately, so
 * there is one place the selection lives. On the very first render that step is
 * itself being seeded, so its own default stands in — it ignores the seed
 * anyway.
 */
const seedFrom = (
  valuesByStep: ValuesByStep,
  firstStepId: DataStoryStepId,
): DataStoryStepSeed => ({
  recipeId:
    (valuesByStep[firstStepId] as RecipePickerValues | undefined)
      ?.selectedRecipeId ?? DEFAULT_RECIPE_ID,
});

/** The first step is seeded up front: it holds the method the request carries. */
const initialValuesByStep = (
  firstStepId: DataStoryStepId,
  initialRecipeId?: DataStoryRecipeId,
): ValuesByStep => {
  const values = getDataStoryStep(firstStepId).initialValues(seedFrom({}, firstStepId));
  return {
    [firstStepId]: initialRecipeId ? { ...(values as object), selectedRecipeId: initialRecipeId } : values,
  };
};

export const useCreateDataStoryFlowInternalState = ({
  open,
  onSubmit,
  workspaceId,
  newPath,
  initialRecipeId,
}: Args) => {
  const firstStepId = newPath ? NEW_PATH_FIRST_STEP_ID : FIRST_STEP_ID;
  const [stepId, setStepId] = useState<DataStoryStepId>(firstStepId);
  const [valuesByStep, setValuesByStep] = useState<ValuesByStep>(() =>
    initialValuesByStep(firstStepId, initialRecipeId),
  );

  // Reopening starts a clean flow — a half-filled form from a dismissed
  // attempt must not resurface.
  useEffect(() => {
    if (!open) return;
    setStepId(firstStepId);
    setValuesByStep(initialValuesByStep(firstStepId, initialRecipeId));
  }, [open, firstStepId, initialRecipeId]);

  const step = getDataStoryStep(stepId);
  const values = valuesByStep[stepId];

  const patchValues = useCallback(
    (patch: object) =>
      setValuesByStep((prev) => ({
        ...prev,
        [stepId]: { ...(prev[stepId] as object), ...patch },
      })),
    [stepId],
  );

  const buildRequest = useCallback(
    (finalValues: ValuesByStep): CreateStoryRequest => {
      const { selectedRecipeId, goal, audiences } = finalValues[
        firstStepId
      ] as RecipePickerValues;
      const detailsId = DETAILS_STEP_IDS.find(
        (id) => finalValues[id] !== undefined,
      );
      // The new path asks the goal on the first step, so an AI story has no details step.
      const goalDetails: StoryDetailsValues | undefined =
        !detailsId && newPath && selectedRecipeId === "ai"
          ? { ...emptyAiStoryDetails(), goal: goal ?? "", audiences: audiences ?? [] }
          : undefined;

      return toCreateStoryRequest(
        selectedRecipeId,
        detailsId
          ? (finalValues[detailsId] as StoryDetailsValues)
          : goalDetails,
        { newPath },
      );
    },
    [firstStepId, newPath],
  );

  const goNext = useCallback(() => {
    const nextId = step.nextStepId(values);

    if (nextId === null) {
      onSubmit(buildRequest(valuesByStep));
      return;
    }

    // Seed the next step only on first arrival, so returning to a step later
    // does not wipe what was already entered there.
    setValuesByStep((prev) =>
      prev[nextId] === undefined
        ? {
            ...prev,
            [nextId]: getDataStoryStep(nextId).initialValues(
              seedFrom(prev, firstStepId),
            ),
          }
        : prev,
    );
    setStepId(nextId);
  }, [buildRequest, firstStepId, onSubmit, step, values, valuesByStep]);

  const canContinue = useMemo(
    () => step.canContinue(values),
    [step, values],
  );

  // Fixed copy for every frame but the agent one, which names the agent picked.
  const title = useMemo(() => resolveStepTitle(step, values), [step, values]);

  // Fixed for every frame but the method one, which says "Create story" when
  // the pick submits from there instead of continuing to a goal.
  const primaryActionLabel = useMemo(
    () => resolveStepPrimaryActionLabel(step, values),
    [step, values],
  );

  // Gated on `open`: the sidebar keeps this modal mounted on every page, so an
  // ungated fetch would hit data-sources and data-models on every page load.
  const { availableSources, modelOptions, isSourcesLoading } =
    useWorkspacePickerData(workspaceId, { enabled: open && !newPath });

  // Named in the footer so the user sees where the story will be made.
  const { workspace } = useWorkspaceDetail(open ? workspaceId || undefined : undefined);

  const popoverBoundaryRef = useRef<HTMLDivElement>(null);

  // A molecule may not own a document listener, so the organism does it and the
  // step says what to close (`closePopovers`). Re-subscribed as the step or its
  // values change, which is what keeps the patch pointed at the current step.
  useEffect(() => {
    const { closePopovers } = step;
    if (!closePopovers) return;

    const handleMouseDown = (event: MouseEvent) => {
      const boundary = popoverBoundaryRef.current;
      if (!boundary || boundary.contains(event.target as Node)) return;

      const patch = closePopovers(values);
      if (patch) patchValues(patch);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [step, values, patchValues]);

  const context = useMemo<DataStoryStepContext>(
    () => ({
      availableSources,
      modelOptions,
      isSourcesLoading,
      popoverBoundaryRef,
      sourcesFromModel: newPath,
    }),
    [availableSources, modelOptions, isSourcesLoading, newPath],
  );

  return {
    step,
    title,
    primaryActionLabel,
    values,
    workspaceName: workspace?.name,
    patchValues,
    goNext,
    canContinue,
    context,
  };
};
